import cx from 'clsx';
import * as React from 'react';

import { useWindowSize } from '../../../hooks/useWindowSize';
import { CallbackMode } from '../../../logic/main/types';
import { QuicksilverTileOutfitType } from '../../game-tile/index';
import ClosedOverlay from '../ClosedOverlay';
import { MEDIUM_BREAKPOINT, MEDIUM_OUTFIT } from '../constants';
import GhostTile from '../GhostTile';
import ModularGridGameTile from '../ModularGridGameTile';
import {
  GameClickProps,
  GameTileProps,
  GridSizeType,
  OverlayProps,
} from '../types';
import { prepareGameList, selectProperStyles } from '../utils';
import styles from './Grid.module.scss';

interface GridProps {
  gameList?: GameTileProps[];
  onClick: (args: { gamePosition: number; game: GameClickProps }) => void;
  onInfoClick: (args: {
    gamePosition: number;
    game: GameClickProps;
    path: string;
  }) => void;
  itemsPerRow?: number;
  gamesLimit?: number;
  percentageWidth?: number;
  insideHeader?: boolean;
  overlay?: OverlayProps;
  ghostItems?: number;
  gameDetailsInfo?: boolean;
  callbackMode: CallbackMode;
  size?: GridSizeType;
  initialIndex?: number;
  type?: 'jackpotNine' | 'grid' | 'collection';
}

const Grid: React.FC<GridProps> = ({
  gameList = [],
  onClick,
  onInfoClick,
  gamesLimit,
  percentageWidth = 100,
  insideHeader = true,
  overlay,
  ghostItems,
  gameDetailsInfo,
  callbackMode,
  size,
  initialIndex = 0,
  type = 'grid',
}) => {
  const width = useWindowSize().width;
  if (!gameList.length && !overlay) return null;

  const isJackpotLarge =
    type === 'jackpotNine' &&
    (size === 'medium' || width >= MEDIUM_BREAKPOINT.min);

  const preparedGameList = prepareGameList(
    isJackpotLarge ? gameList.slice(1) : gameList,
    gamesLimit
  );
  const gamePosition = isJackpotLarge ? 2 : 1;

  const handleClick = (gamePosition: number, fn: Function) => (
    game: GameClickProps,
    path?: string
  ) => {
    fn({ game, gamePosition, ...(path && { path }) });
  };
  let slotPercentageSize = 0;

  if (percentageWidth !== 100) {
    slotPercentageSize = 100 / gameList.length;
  }
  const shouldWrap = !(percentageWidth < 100);
  const ghostItemsArray = Array.from(Array(ghostItems).keys());

  const containerClassName = cx(
    styles.container,
    insideHeader && styles.insideHeader,
    shouldWrap ? styles.wrap : styles.nowrap
  );

  if (overlay) {
    return (
      <div className={containerClassName}>
        {ghostItemsArray.map(() => (
          <div
            className={styles.slot}
            style={{
              flexBasis: slotPercentageSize && `${slotPercentageSize}%`,
            }}
          >
            <GhostTile />
          </div>
        ))}
        <ClosedOverlay {...overlay} />
      </div>
    );
  }

  const createSlot = (
    position: number,
    outfit: QuicksilverTileOutfitType,
    callbackMode: CallbackMode,
    slot?: GameTileProps
  ) => (
    <ModularGridGameTile
      data-testid="modular-grid-tile"
      game={slot}
      onClick={handleClick(position, onClick)}
      onInfoClick={handleClick(position, onInfoClick)}
      outfit={outfit}
      gameDetailsInfo={gameDetailsInfo}
      isLazy
      isAnchor
      callbackMode={callbackMode}
    />
  );

  return (
    <div className={containerClassName} data-testid="grid-grid">
      {isJackpotLarge && (
        <div
          className={cx(
            selectProperStyles(
              {
                small: styles.slotBigSmallForced,
                medium: styles.slotBigMediumForced,
                default: [styles.slotBigSmall, styles.slotBigMedium],
              },
              size
            )
          )}
          data-testid={`1-slot`}
          key={1}
        >
          {createSlot(
            1 + initialIndex,
            MEDIUM_OUTFIT,
            callbackMode,
            gameList[0]
          )}
        </div>
      )}
      <div
        className={cx(
          type === 'jackpotNine' &&
            selectProperStyles(
              {
                small: styles.rowJackpotSmallForced,
                medium: styles.rowJackpotMediumForced,
                default: [styles.rowJackpotSmall, styles.rowJackpotMedium],
              },
              size
            ),
          {
            [styles.rowGrid]: type === 'grid' || type === 'collection',
            [styles.rowJackpot]: type === 'jackpotNine',
          }
        )}
      >
        {preparedGameList.map((item: GameTileProps, index: number) => (
          <div
            style={
              percentageWidth !== 100 || !gamesLimit
                ? {
                    flexBasis: slotPercentageSize && `${slotPercentageSize}%`,
                  }
                : {}
            }
            className={cx(
              type === 'jackpotNine' &&
                selectProperStyles(
                  {
                    small: styles.slotJackpotSmallForced,
                    medium: styles.slotJackpotMediumForced,
                    default: [
                      styles.slotJackpotSmall,
                      styles.slotJackpotMedium,
                    ],
                  },
                  size
                ),
              type === 'grid' &&
                selectProperStyles(
                  {
                    small: styles.slotGridSmallForced,
                    medium: styles.slotGridMediumForced,
                    default: [styles.slotGridSmall, styles.slotGridMedium],
                  },
                  size
                ),
              type === 'collection' &&
                selectProperStyles(
                  {
                    small: styles.slotCollectionSmallForced,
                    medium: styles.slotCollectionMediumForced,
                    default: [
                      styles.slotCollectionSmall,
                      styles.slotCollectionMedium,
                    ],
                  },
                  size
                ),
              styles.slot
            )}
            data-testid={`${index + gamePosition}-slot`}
            key={index}
          >
            {createSlot(
              index + gamePosition + initialIndex,
              MEDIUM_OUTFIT,
              callbackMode,
              item
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
export default Grid;
